import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  type Level,
  type Marker,
  type MatrixInteraction,
} from "@/lib/matrix-risk";
import { loadMatrixData, type MatrixQueryClient } from "@/lib/matrix-load";

export type { Level, Marker, MatrixInteraction };
export { RISK_TO_LEVEL, riskToLevel, findInteraction } from "@/lib/matrix-risk";

export type MatrixDataState = {
  substances: string[];
  interactions: MatrixInteraction[];
  loading: boolean;
  /** Technical load failure. Never conflated with the scientific "unknown" level. */
  loadError: boolean;
  /** Risk values outside the database enum, shown as a validation warning. */
  invalidRiskValues: string[];
  /** Interaction rows with unresolvable substance ids. */
  unresolvedRows: number;
};

export function useMatrixData(): MatrixDataState {
  const [state, setState] = useState<MatrixDataState>({
    substances: [],
    interactions: [],
    loading: true,
    loadError: false,
    invalidRiskValues: [],
    unresolvedRows: 0,
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await loadMatrixData(supabase as unknown as MatrixQueryClient);
        if (!mounted) return;
        setState({
          substances: result.substances,
          interactions: result.interactions,
          loadError: result.loadError,
          invalidRiskValues: result.invalidRiskValues,
          unresolvedRows: result.unresolvedRows,
          loading: false,
        });
      } catch {
        if (!mounted) return;
        setState({
          substances: [],
          interactions: [],
          loadError: true,
          invalidRiskValues: [],
          unresolvedRows: 0,
          loading: false,
        });
      } finally {
        // No endless spinner, even if an unexpected failure slipped through.
        if (mounted) setState((prev) => (prev.loading ? { ...prev, loading: false } : prev));
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return state;
}
